import React, { useState, useRef } from "react";
import "./MultiImageUpload.scss";

const MultiImageUpload = ({ onImagesUploaded, initialImages = [], maxImages = 5 }) => {
    const [selectedImages, setSelectedImages] = useState([]);
    const fileInputRef = useRef(null);
    const initializedRef = useRef(false);

    // initialImages가 채워지면 최초 1회만 초기화
    React.useEffect(() => {
        if (!initializedRef.current && initialImages.length > 0) {
            initializedRef.current = true;
            setSelectedImages(
                initialImages.map((url, i) => ({
                    id: `existing-${i}-${url}`,
                    preview: url,
                    file: null,
                    isExisting: true,
                }))
            );
        }
    }, [initialImages]);

    const handleRemoveImage = (imageId) => {
        setSelectedImages((prev) => {
            const imageToRemove = prev.find((img) => img.id === imageId);
            if (imageToRemove?.preview && !imageToRemove.isExisting) {
                URL.revokeObjectURL(imageToRemove.preview);
            }

            const remaining = prev.filter((img) => img.id !== imageId);
            const remainingFiles = remaining.filter((img) => !img.isExisting).map((img) => img.file);
            const remainingExistingUrls = remaining.filter((img) => img.isExisting).map((img) => img.preview);
            onImagesUploaded?.(remainingFiles, remainingExistingUrls);

            return remaining;
        });
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);

        // 최대 이미지 개수 체크
        if (selectedImages.length + files.length > maxImages) {
            alert(`최대 ${maxImages}개의 이미지만 선택할 수 있습니다.`);
            return;
        }

        // 이미지 파일 검증
        const validFiles = files.filter((file) => {
            if (!file.type.startsWith("image/")) {
                alert(`${file.name}은(는) 이미지 파일이 아닙니다.`);
                return false;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert(`${file.name}의 크기가 5MB를 초과합니다.`);
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        // 미리보기 URL 생성
        const newImages = validFiles.map((file, idx) => ({
            file: file,
            id: Date.now() + Math.random(),
            preview: URL.createObjectURL(file),
            status: "uploaded", // 로컬에서만 처리하므로 바로 uploaded 상태
            order: idx + 1,
        }));

        setSelectedImages((prev) => {
            const updatedImages = [...prev, ...newImages];
            const allNewFiles = updatedImages.filter((img) => !img.isExisting).map((img) => img.file);
            const allExistingUrls = updatedImages.filter((img) => img.isExisting).map((img) => img.preview);
            onImagesUploaded(allNewFiles, allExistingUrls);

            return updatedImages;
        });

        // 파일 입력 초기화
        event.target.value = "";
    };

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleImageClick();
        }
    };

    const handleRemoveImageKeyDown = (event, imageId) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleRemoveImage(imageId);
        }
    };

    return (
        <div className="multi-image-upload">
            <div className="image-upload-area">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="file-input"
                />

                <div
                    className="upload-button"
                    onClick={handleImageClick}
                    onKeyDown={handleImageKeyDown}
                    tabIndex={0}
                    role="button"
                    aria-label="이미지 선택"
                >
                    <div className="upload-icon">+</div>
                    <p>클릭하여 이미지를 선택하세요</p>
                </div>
            </div>

            {selectedImages.length > 0 && (
                <div className="image-preview-container">
                    <div className="image-grid">
                        {selectedImages.map((image) => (
                            <div key={image.id} className="image-item">
                                <div className="image-preview">
                                    <img src={image.preview} alt="미리보기" />
                                    <button
                                        className="remove-btn"
                                        onClick={() =>
                                            handleRemoveImage(image.id)
                                        }
                                        onKeyDown={(e) =>
                                            handleRemoveImageKeyDown(
                                                e,
                                                image.id
                                            )
                                        }
                                        tabIndex={0}
                                        aria-label="이미지 제거"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiImageUpload;
